import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Send,
  CreditCard,
  Ban,
  Download,
  Copy,
  Check,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  invoicesService,
  type Invoice,
  type CreateInvoiceLineItemInput,
} from '@/services/invoicesService';
import {
  type PracticeProduct,
} from '@/services/practiceProductsService';
import PracticeProductPicker from './PracticeProductPicker';
import SearchablePatientSelect from '@/components/clinic/SearchablePatientSelect';
import type { Patient } from '@/lib/types';
import { INVOICE_STATUS_COLORS, formatCents } from '@/lib/invoiceFormat';

interface InvoiceEditorProps {
  clinicId: string;
  invoice: Invoice | null;
  onClose: () => void;
  onVoid: (invoiceId: string, reason?: string) => Promise<Invoice | null>;
  onDownloadPdf: (invoiceId: string) => Promise<void>;
  defaultPatient?: Patient | null;
}

interface SavedPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
}

export default function InvoiceEditor({
  clinicId,
  invoice: initialInvoice,
  onClose,
  onVoid,
  onDownloadPdf,
  defaultPatient,
}: InvoiceEditorProps) {
  const { toast } = useToast();
  const [invoice, setInvoice] = useState<Invoice | null>(initialInvoice);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(initialInvoice?.notes || '');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(defaultPatient || null);
  const [draftLineItems, setDraftLineItems] = useState<CreateInvoiceLineItemInput[]>([]);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<SavedPaymentMethod[]>([]);

  // Send / Copy payment link
  const [sendingLink, setSendingLink] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogUrl, setLinkDialogUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Charge flow
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [chargeStep, setChargeStep] = useState<'select' | 'confirm'>('select');
  const [chargeMethodId, setChargeMethodId] = useState<string | null>(null);
  const [charging, setCharging] = useState(false);

  // Void flow
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // PDF download
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // For new invoices
  const [isNew] = useState(!initialInvoice);

  const reload = useCallback(async () => {
    if (!invoice?.id) return;
    try {
      const fresh = await invoicesService.getById(clinicId, invoice.id);
      setInvoice(fresh);
      setNotes(fresh.notes || '');
    } catch {
      toast({ title: 'Error', description: 'Failed to reload invoice', variant: 'destructive' });
    }
  }, [clinicId, invoice?.id, toast]);

  // Hydrate line items + transactions on mount for existing invoices. The
  // list endpoint doesn't include associations, so auto-invoices created
  // at booking would show empty line items otherwise.
  useEffect(() => {
    if (!initialInvoice?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await invoicesService.getById(clinicId, initialInvoice.id);
        if (!cancelled) {
          setInvoice(fresh);
          setNotes(fresh.notes || '');
        }
      } catch {
        // leave the list-provided invoice in place
      }
    })();
    return () => { cancelled = true; };
  }, [clinicId, initialInvoice?.id]);

  // Fetch the patient's saved payment methods so the Charge button can
  // appear when an authorized card is on file. Failures are non-fatal.
  useEffect(() => {
    const patientId = invoice?.patient_id || defaultPatient?.id;
    if (!clinicId || !patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const methods = await invoicesService.getPatientPaymentMethods(clinicId, patientId);
        if (!cancelled) setSavedPaymentMethods(methods);
      } catch {
        if (!cancelled) setSavedPaymentMethods([]);
      }
    })();
    return () => { cancelled = true; };
  }, [clinicId, invoice?.patient_id, defaultPatient?.id]);

  const handleCreateInvoice = async () => {
    if (!selectedPatient?.id) {
      toast({ title: 'Error', description: 'Please select a patient', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const created = await invoicesService.create(clinicId, {
        patientId: selectedPatient.id,
        lineItems: draftLineItems.length > 0 ? draftLineItems : undefined,
        notes: notes || undefined,
      });
      setInvoice(created);
      toast({ title: 'Created', description: 'Invoice created as draft' });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.error?.message || 'Failed to create invoice', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddDraftLineItem = (input: CreateInvoiceLineItemInput) => {
    setDraftLineItems((prev) => [...prev, input]);
  };

  const handleAddDraftFromProduct = (product: PracticeProduct) => {
    handleAddDraftLineItem({
      description: product.name,
      unitPriceCents: product.price_cents,
      quantity: 1,
      cptCode: product.default_cpt_code || undefined,
      icdCodes: product.default_icd_codes || undefined,
      practiceProductId: product.id,
    });
  };

  const handleRemoveDraftLineItem = (index: number) => {
    setDraftLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const draftSubtotalCents = draftLineItems.reduce(
    (sum, li) => sum + Math.round((li.quantity || 1) * (li.unitPriceCents || 0)),
    0,
  );

  const handleAddLineItem = async (input: CreateInvoiceLineItemInput) => {
    if (!invoice?.id) return;
    setSaving(true);
    try {
      await invoicesService.addLineItem(clinicId, invoice.id, input);
      await reload();
    } catch {
      toast({ title: 'Error', description: 'Failed to add line item', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLineItem = async (lineItemId: string) => {
    if (!invoice?.id) return;
    try {
      await invoicesService.removeLineItem(clinicId, invoice.id, lineItemId);
      await reload();
    } catch {
      toast({ title: 'Error', description: 'Failed to remove line item', variant: 'destructive' });
    }
  };

  const handleAddFromProduct = (product: PracticeProduct) => {
    handleAddLineItem({
      description: product.name,
      unitPriceCents: product.price_cents,
      quantity: 1,
      cptCode: product.default_cpt_code || undefined,
      icdCodes: product.default_icd_codes || undefined,
      practiceProductId: product.id,
    });
  };

  const handleSaveNotes = async () => {
    if (!invoice?.id) return;
    try {
      await invoicesService.update(clinicId, invoice.id, { notes });
      toast({ title: 'Saved', description: 'Invoice notes updated' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save notes', variant: 'destructive' });
    }
  };

  const isEditable = invoice?.status === 'draft' || invoice?.status === 'open';
  const isVoidable = invoice && ['draft', 'open', 'sent'].includes(invoice.status);
  const canBill =
    invoice &&
    !['paid', 'void', 'uncollectible'].includes(invoice.status) &&
    invoice.total_cents > 0 &&
    invoice.balance_cents > 0;
  const canCharge = canBill && savedPaymentMethods.length > 0;

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast({ title: 'Copied', description: 'Payment link copied to clipboard' });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast({ title: 'Error', description: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const handleSendPaymentLink = async () => {
    if (!invoice?.id) return;
    setSendingLink(true);
    try {
      const result = await invoicesService.sendPaymentLink(clinicId, invoice.id, ['email', 'sms']);
      setLinkDialogUrl(result.paymentUrl);
      setLinkDialogOpen(true);
      const description = result.sent?.length
        ? `Payment link sent via ${result.sent.join(' and ')}`
        : 'Payment link sent';
      toast({ title: 'Sent', description });
      await reload();
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.message || 'Failed to send payment link';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSendingLink(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!invoice?.id) return;
    setSendingLink(true);
    try {
      const { paymentUrl } = await invoicesService.getPaymentLink(clinicId, invoice.id);
      await copyToClipboard(paymentUrl);
      await reload();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'Failed to get payment link', variant: 'destructive' });
    } finally {
      setSendingLink(false);
    }
  };

  const openChargeDialog = () => {
    setChargeMethodId(savedPaymentMethods[0]?.id || null);
    setChargeStep('select');
    setChargeDialogOpen(true);
  };

  const handleConfirmCharge = async () => {
    if (!invoice?.id || !chargeMethodId) return;
    setCharging(true);
    try {
      await invoicesService.charge(clinicId, invoice.id, chargeMethodId);
      toast({ title: 'Charged', description: 'Payment processed successfully' });
      setChargeDialogOpen(false);
      await reload();
    } catch (err: any) {
      toast({ title: 'Payment failed', description: err?.message || 'Card was not charged', variant: 'destructive' });
    } finally {
      setCharging(false);
    }
  };

  const chargeSelectedMethod = savedPaymentMethods.find((m) => m.id === chargeMethodId);

  const handleConfirmVoid = async () => {
    if (!invoice?.id) return;
    setVoiding(true);
    try {
      const updated = await onVoid(invoice.id, voidReason.trim() || undefined);
      if (updated) {
        setInvoice((prev) => (prev ? { ...prev, ...updated } : prev));
      }
      setVoidDialogOpen(false);
      setVoidReason('');
    } finally {
      setVoiding(false);
    }
  };

  const handleClickPdf = async () => {
    if (!invoice?.id || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      await onDownloadPdf(invoice.id);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatPatientDob = (dob?: string | null) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  };

  const patientFullName = invoice?.patient
    ? [invoice.patient.first_name, invoice.patient.middle_name, invoice.patient.last_name]
        .filter(Boolean)
        .join(' ')
    : null;
  const patientDob = formatPatientDob(invoice?.patient?.dob);
  const patientContact = [invoice?.patient?.email, invoice?.patient?.phone].filter(Boolean).join(' · ');

  const actionsBusy = voiding || downloadingPdf || sendingLink || charging;

  // New invoice creation form
  if (isNew && !invoice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">New Invoice</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Patient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {defaultPatient ? (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                {defaultPatient.full_name || `${defaultPatient.first_name} ${defaultPatient.last_name}`}
              </div>
            ) : (
              <>
                <Label>Patient *</Label>
                <SearchablePatientSelect
                  onValueChange={(p) => setSelectedPatient(p)}
                  placeholder="Search patients by name..."
                  hasError={!selectedPatient}
                />
                {selectedPatient && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {selectedPatient.full_name || `${selectedPatient.first_name} ${selectedPatient.last_name}`}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            <PracticeProductPicker
              clinicId={clinicId}
              onSelect={handleAddDraftFromProduct}
            />
          </CardHeader>
          <CardContent>
            {draftLineItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No line items yet. Add items from your practice products catalog or manually below.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-2">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">CPT</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit Price</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {draftLineItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center px-2 py-2 border rounded-md">
                    <div className="col-span-4 text-sm">{item.description}</div>
                    <div className="col-span-2 text-sm text-muted-foreground">{item.cptCode || '-'}</div>
                    <div className="col-span-1 text-sm text-right">{item.quantity || 1}</div>
                    <div className="col-span-2 text-sm text-right">{formatCents(item.unitPriceCents)}</div>
                    <div className="col-span-2 text-sm text-right font-medium">
                      {formatCents(Math.round((item.quantity || 1) * (item.unitPriceCents || 0)))}
                    </div>
                    <div className="col-span-1 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleRemoveDraftLineItem(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2 text-sm font-medium">
                  Subtotal: {formatCents(draftSubtotalCents)}
                </div>
              </div>
            )}
            <ManualLineItemForm onAdd={handleAddDraftLineItem} saving={false} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Invoice notes (optional)"
              rows={3}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreateInvoice} disabled={saving || !selectedPatient?.id}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Invoice
          </Button>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              Invoice {invoice.invoice_number || 'DRAFT'}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={INVOICE_STATUS_COLORS[invoice.status] || 'bg-gray-100'}>
                {invoice.status.replace('_', ' ')}
              </Badge>
              <span className="text-sm text-muted-foreground capitalize">
                {invoice.source.replace(/_/g, ' ')}
              </span>
              {patientFullName && (
                <span className="text-sm text-muted-foreground">
                  | {patientFullName}
                </span>
              )}
            </div>
            {invoice.patient && (patientDob || patientContact) && (
              <p className="text-xs text-muted-foreground mt-1">
                {[
                  patientDob ? `DOB ${patientDob}` : null,
                  invoice.patient.gender ? invoice.patient.gender : null,
                  patientContact || null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {canBill && (
            <>
              <Button onClick={handleSendPaymentLink} disabled={actionsBusy}>
                {sendingLink ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send Payment Link
              </Button>
              <Button variant="outline" onClick={handleCopyPaymentLink} disabled={actionsBusy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
              </Button>
            </>
          )}
          {canCharge && (
            <Button variant="outline" onClick={openChargeDialog} disabled={actionsBusy}>
              <CreditCard className="mr-2 h-4 w-4" />
              Charge Saved Card
            </Button>
          )}
          {isVoidable && (
            <Button
              variant="destructive"
              onClick={() => setVoidDialogOpen(true)}
              disabled={actionsBusy}
            >
              {voiding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />}
              Void
            </Button>
          )}
          {invoice.invoice_number && (
            <Button variant="outline" onClick={handleClickPdf} disabled={actionsBusy}>
              {downloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              PDF
            </Button>
          )}
        </div>
      </div>

      {invoice.status === 'void' && (invoice.metadata?.void_reason || invoice.metadata?.voided_at) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-900">Void Details</p>
          {invoice.metadata.void_reason && (
            <p className="text-sm text-amber-800 mt-1">Reason: {String(invoice.metadata.void_reason)}</p>
          )}
          {invoice.metadata.voided_at && (
            <p className="text-xs text-amber-600 mt-1">
              Voided {new Date(String(invoice.metadata.voided_at)).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Totals summary */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Subtotal</p>
            <p className="text-2xl font-bold">{formatCents(invoice.subtotal_cents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{formatCents(invoice.total_cents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Paid</p>
            <p className="text-2xl font-bold text-green-600">{formatCents(invoice.amount_paid_cents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Balance Due</p>
            <p className={`text-2xl font-bold ${invoice.balance_cents > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCents(invoice.balance_cents)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Line Items</CardTitle>
          {isEditable && (
            <PracticeProductPicker
              clinicId={clinicId}
              onSelect={handleAddFromProduct}
            />
          )}
        </CardHeader>
        <CardContent>
          {(!invoice.lineItems || invoice.lineItems.length === 0) ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No line items yet. Add items from your practice products catalog or manually.
            </p>
          ) : (
            <div className="space-y-2">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-2">
                <div className="col-span-4">Description</div>
                <div className="col-span-2">CPT</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Unit Price</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-1"></div>
              </div>

              {invoice.lineItems.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-2 items-center px-2 py-2 border rounded-md">
                  <div className="col-span-4 text-sm">{item.description}</div>
                  <div className="col-span-2 text-sm text-muted-foreground">{item.cpt_code || '-'}</div>
                  <div className="col-span-1 text-sm text-right">{item.quantity}</div>
                  <div className="col-span-2 text-sm text-right">{formatCents(item.unit_price_cents)}</div>
                  <div className="col-span-2 text-sm text-right font-medium">{formatCents(item.amount_cents)}</div>
                  <div className="col-span-1 text-right">
                    {isEditable && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleRemoveLineItem(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Manual line item add */}
          {isEditable && <ManualLineItemForm onAdd={handleAddLineItem} saving={saving} />}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add CPT/ICD notes, special instructions, etc."
            rows={3}
            disabled={!isEditable}
          />
          {isEditable && (
            <Button variant="outline" size="sm" className="mt-2" onClick={handleSaveNotes}>
              Save Notes
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      {invoice.transactions && invoice.transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoice.transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{describePaymentMethod(tx)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(tx.processed_at).toLocaleString()}
                      {tx.stripe_charge_id && (
                        <span className="ml-2 font-mono">· {tx.stripe_charge_id.slice(-8)}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">${tx.amount.toFixed(2)}</p>
                    <Badge className={transactionBadgeClass(tx.status)}>
                      {tx.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send/Copy payment link confirmation dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment link ready</DialogTitle>
            <DialogDescription>
              The patient has been notified. You can also share the link directly below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
            <Input readOnly value={linkDialogUrl || ''} className="font-mono text-xs" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => linkDialogUrl && copyToClipboard(linkDialogUrl)}
            >
              {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Charge saved card dialog (2-step: select -> confirm) */}
      <Dialog
        open={chargeDialogOpen}
        onOpenChange={(open) => {
          setChargeDialogOpen(open);
          if (!open) {
            setChargeStep('select');
            setChargeMethodId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          {chargeStep === 'select' && (
            <>
              <DialogHeader>
                <DialogTitle>Charge saved card</DialogTitle>
                <DialogDescription>
                  Select a card on file to charge {formatCents(invoice.balance_cents)}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                {savedPaymentMethods.map((pm) => (
                  <label
                    key={pm.id}
                    className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer ${
                      chargeMethodId === pm.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'hover:bg-muted/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="charge-pm"
                      value={pm.id}
                      checked={chargeMethodId === pm.id}
                      onChange={() => setChargeMethodId(pm.id)}
                    />
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-medium capitalize">
                        {pm.brand} ending in {pm.last4}
                      </p>
                      {pm.expMonth && pm.expYear && (
                        <p className="text-xs text-muted-foreground">
                          Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setChargeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button disabled={!chargeMethodId} onClick={() => setChargeStep('confirm')}>
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}
          {chargeStep === 'confirm' && chargeSelectedMethod && (
            <>
              <DialogHeader>
                <DialogTitle>Confirm payment</DialogTitle>
                <DialogDescription>
                  Charge {formatCents(invoice.balance_cents)} to{' '}
                  <span className="font-medium capitalize">{chargeSelectedMethod.brand}</span> ending in{' '}
                  <span className="font-mono">{chargeSelectedMethod.last4}</span>? This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setChargeStep('select')} disabled={charging}>
                  Back
                </Button>
                <Button onClick={handleConfirmCharge} disabled={charging}>
                  {charging && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Charge {formatCents(invoice.balance_cents)}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Void confirmation */}
      <AlertDialog
        open={voidDialogOpen}
        onOpenChange={(open) => {
          if (voiding) return;
          setVoidDialogOpen(open);
          if (!open) setVoidReason('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Voiding marks invoice {invoice.invoice_number || 'DRAFT'} as uncollectible and cancels any pending
              payment intent. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="void-reason" className="text-xs">Reason (optional)</Label>
            <Textarea
              id="void-reason"
              rows={3}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Duplicate invoice, billed in error"
              disabled={voiding}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmVoid();
              }}
              disabled={voiding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Void invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function describePaymentMethod(tx: { card_brand?: string | null; card_last4?: string | null; payment_method?: string | null }) {
  if (tx.card_brand && tx.card_last4) {
    const brand = tx.card_brand.charAt(0).toUpperCase() + tx.card_brand.slice(1);
    return `${brand} ···· ${tx.card_last4}`;
  }
  if (tx.payment_method) {
    return tx.payment_method
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return 'Payment';
}

function transactionBadgeClass(status: string) {
  switch (status) {
    case 'succeeded':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'processing':
      return 'bg-amber-100 text-amber-800';
    case 'refunded':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
      return 'bg-gray-200 text-gray-800';
    case 'failed':
    default:
      return 'bg-red-100 text-red-800';
  }
}

// Inline manual line item form (zod-validated)
const lineItemSchema = z.object({
  description: z
    .string()
    .trim()
    .min(1, { message: 'Description is required' })
    .max(500, { message: 'Max 500 characters' }),
  quantity: z
    .coerce
    .number({ invalid_type_error: 'Quantity must be a number' })
    .positive({ message: 'Must be greater than 0' })
    .max(9999, { message: 'Max 9999' }),
  unitPriceDollars: z
    .coerce
    .number({ invalid_type_error: 'Price must be a number' })
    .min(0, { message: 'Must be ≥ 0' })
    .max(999999.99, { message: 'Max $999,999.99' }),
  cptCode: z
    .string()
    .trim()
    .regex(/^[0-9A-Z]{4,5}$/, { message: '4–5 chars, A–Z or 0–9' })
    .or(z.literal(''))
    .optional(),
});

type LineItemFormValues = z.infer<typeof lineItemSchema>;

function ManualLineItemForm({
  onAdd,
  saving,
}: {
  onAdd: (input: CreateInvoiceLineItemInput) => void;
  saving: boolean;
}) {
  const form = useForm<LineItemFormValues>({
    resolver: zodResolver(lineItemSchema),
    mode: 'onChange',
    defaultValues: {
      description: '',
      quantity: 1,
      unitPriceDollars: 0,
      cptCode: '',
    },
  });

  const handleSubmit = (values: LineItemFormValues) => {
    onAdd({
      description: values.description.trim(),
      quantity: values.quantity,
      unitPriceCents: Math.round(values.unitPriceDollars * 100),
      cptCode: values.cptCode ? values.cptCode.toUpperCase() : undefined,
    });
    form.reset({ description: '', quantity: 1, unitPriceDollars: 0, cptCode: '' });
  };

  return (
    <div className="mt-4 pt-4 border-t">
      <p className="text-sm font-medium mb-2">Add Manual Line Item</p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="grid grid-cols-12 gap-2 items-start">
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="col-span-4">
                <FormLabel className="text-xs">Description</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Service description" className="h-9" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cptCode"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">CPT Code</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="99213" className="h-9" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem className="col-span-1">
                <FormLabel className="text-xs">Qty</FormLabel>
                <FormControl>
                  <Input type="number" min={1} step="1" className="h-9" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="unitPriceDollars"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">Unit Price ($)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" placeholder="0.00" className="h-9" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="col-span-3 pt-5">
            <Button
              size="sm"
              type="submit"
              disabled={saving || !form.formState.isValid}
              className="h-9"
            >
              {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Plus className="mr-1 h-3 w-3" />}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
